// The command registry: the only place a command is defined. The keymap, the palette, the
// menus, the toolbar, and the shortcuts sheet all read it. A command is an id, a title, its
// place in the menus, a `when` over the shell, and a `run`; its chord comes from the chord
// table by id: the shell's, or the one a page with commands of its own passes.

import { chordIn, editorChordIn, type ChordOverrides, type ChordTable, type Platform } from './bindings.ts'
import { CHORDS } from './chords.ts'
import type { MenuTable, Placement, ShellMenus } from './menu.ts'
import type { PaletteEntry, PaletteMeta, Screen } from './palette.ts'

export interface Command<S, T extends MenuTable = ShellMenus> {
  id: string
  title: string
  /// Where the menus list it, or why they do not (docs/design/menu-bar.md, decision 1).
  menu: Placement<S, T>
  when?: (shell: S) => boolean
  /// Why the command is unavailable while `when` says so, as its menu item says under its label
  /// in the page and in the native menu (brief 53); null for no reason worth saying.
  reason?: (shell: S) => string | null
  run: (shell: S) => unknown
  /// One sentence for a control whose purpose its label does not make plain. The tooltip and the
  /// shortcuts sheet read it from here, so it is written once.
  help?: string
  /// How the command-palette lists it, or that it does not (core/palette.ts). Set by
  /// `Registry.describe`, so a command's line stays about what it does.
  palette?: PaletteMeta<S>
}

export class Registry<S, T extends MenuTable = ShellMenus> {
  private readonly commands: Command<S, T>[] = []
  overrides: ChordOverrides = {}

  /// Who draws the menus. Where the system does, it binds the native-only chords and dispatches
  /// every accelerator itself, so the page's keymap leaves those to it (decision 6). `table` is
  /// the chord table the commands' chords come from.
  constructor(readonly menus: 'page' | 'system' = 'page', readonly table: ChordTable = CHORDS) {}

  add(command: Command<S, T>): this {
    if (this.commands.some(c => c.id === command.id)) throw new Error('command defined twice: ' + command.id)
    this.commands.push(command)
    return this
  }

  /// Puts the commands made from data, such as the named layouts, in place of the ones made
  /// before under the same id prefix, right after the command `after`.
  setMade(prefix: string, after: string, commands: readonly Command<S, T>[]): void {
    const kept = this.commands.filter(c => !c.id.startsWith(prefix))
    const at = kept.findIndex(c => c.id === after) + 1
    this.commands.splice(0, this.commands.length, ...kept.slice(0, at), ...commands, ...kept.slice(at))
  }

  all(): Command<S, T>[] {
    return [...this.commands]
  }

  get(id: string): Command<S, T> | undefined {
    return this.commands.find(c => c.id === id)
  }

  chord(id: string, platform: Platform): string | null {
    return chordIn(this.table, id, platform, this.overrides, this.menus)
  }

  /// Commands bound to the chord on the platform, in registry order, the editor's own left out.
  bound(chord: string, platform: Platform): Command<S, T>[] {
    return this.commands.filter(c => !editorChordIn(this.table, c.id) && this.chord(c.id, platform) === chord)
  }

  /// Whether the page's keymap dispatches the command's chord, which the system menu does for an
  /// accelerator where it draws the menus.
  private keyed(command: Command<S, T>): boolean {
    return !(this.menus === 'system' && command.menu.kind === 'item' && command.menu.accelerator)
  }

  /// Every chord the document keymap or the editor's plugin should listen for.
  chords(platform: Platform): string[] {
    const out = new Set<string>()
    for (const c of this.commands) {
      const chord = this.chord(c.id, platform)
      if (chord && !editorChordIn(this.table, c.id) && this.keyed(c)) out.add(chord)
    }
    return [...out]
  }

  available(shell: S): Command<S, T>[] {
    return this.commands.filter(c => !c.when || c.when(shell))
  }

  /// The first command bound to the chord whose `when` holds, or null.
  match(chord: string, platform: Platform, shell: S): Command<S, T> | null {
    return this.bound(chord, platform).find(c => this.keyed(c) && (!c.when || c.when(shell))) ?? null
  }

  /// Runs the first command bound to the chord whose `when` holds. False when none did.
  dispatch(chord: string, platform: Platform, shell: S): boolean {
    const command = this.match(chord, platform, shell)
    if (!command) return false
    command.run(shell)
    return true
  }

  /// Runs a command by id when its `when` holds and answers with its completion, so a caller
  /// can wait for a command that plans and sends before it reads the result. Null when the
  /// `when` does not hold.
  run(id: string, shell: S): Promise<unknown> | null {
    const command = this.get(id)
    if (!command) throw new Error('no command ' + id)
    if (command.when && !command.when(shell)) return null
    return Promise.resolve(command.run(shell))
  }

  /// Gives a command its palette line. A command that does not exist is a mistake in the table.
  describe(id: string, meta: PaletteMeta<S>): void {
    const command = this.get(id)
    if (!command) throw new Error('no command ' + id)
    command.palette = meta
  }

  /// The commands the palette can list now: available, and not marked hidden. A command with no
  /// palette line lists under its title. `isAvailable` is the `when` check unless a caller
  /// supplies its own.
  entries(shell: S, screen: Screen, whereOf: (command: Command<S, T>) => string | null, isAvailable: (command: Command<S, T>) => boolean = c => !c.when || c.when(shell)): PaletteEntry[] {
    return this.commands.flatMap((c, order) => {
      const meta = c.palette
      if (meta && 'hidden' in meta) return []
      if (!isAvailable(c)) return []
      const suggested = meta?.suggest?.(shell, screen) ?? null
      const where = typeof meta?.where === 'function' ? meta.where(shell) : meta?.where ?? whereOf(c)
      return [{
        id: c.id,
        label: meta?.label ?? c.title,
        aliases: meta?.aliases ?? [],
        where,
        note: meta?.note?.(shell) ?? null,
        near: meta?.near?.(shell) ?? suggested !== null,
        order,
        suggested,
        selection: meta?.forSelection ?? null,
      }]
    })
  }
}
